import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import {
    FormControl,
    FormGroup,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { NumberUtils, USER_INPUT_DEBOUNCE_TIME } from '@modeso/aumico-lib-core-fe';
import { ViewMode } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import Debug from 'debug';
import fastDeepEqual from 'fast-deep-equal';
import { Subscription, debounceTime, distinctUntilChanged } from 'rxjs';
import { IKeyName } from '../../interfaces/IKeyName.interface';
import { IProperty } from './IProperty.interface';
import { IPropertyOption } from './IPropertyOption.interface';
import { ITableWithForm } from './ITableWithForm.interface';
import { ITableWithFormDelegate } from './ITableWithFormDelegate.interface';
import { ITableWithFormOptions } from './ITableWithFormOptions.interface';

const debug = Debug('aumico:aumico-lib-notes-fe:TableWithFormComponent');

@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'aumico-table-with-form',
    templateUrl: 'tableWithForm.component.html',
    styleUrls: ['tableWithForm.component.scss'],
})
export class TableWithFormComponent implements OnInit, OnChanges {
    OPTIONS: ITableWithFormOptions = {
        properties: [],
    };

    @Input() editable = true;
    @Input() mode? = ViewMode.EDIT;

    private _editableHeaders: boolean;
    private _additionalHeaders: IKeyName[];

    @Input() textTitle: string;

    @Input()
    get editableHeaders(): boolean {
        return this._editableHeaders;
    }
    set editableHeaders(value: boolean) {
        if (this.form != null && value) {
            this.form.setControl('headers', this.createHeadersFormGroup());
        } else if (this.form != null && !value) {
            this.form.setControl('headers', undefined);
        }
        this._editableHeaders = value;
    }
    private _hasTotalRow: boolean;
    @Input()
    get viewMode(): ViewMode {
        return this.mode;
    }
    set viewMode(value: ViewMode) {
        this.mode = value;
    }

    @Input() enableAddNewItem = true;
    @Input()
    set hasTotalRow(value: boolean) {
        this._hasTotalRow = value;

        if (this.form && value) {
            this.form.setControl('totalRow', this.createTotalRowFields(), { emitEvent: false });
        } else if (this.form && !value) {
            this.form.setControl('totalRow', undefined);
        }
    }
    get hasTotalRow(): boolean {
        return this._hasTotalRow;
    }

    totalRowTextCell: string;
    @Input() set totalRowText(value: string) {
        this.totalRowTextCell = value;
    }

    @Input() public set options(options: ITableWithFormOptions) {
        if (options) {
            this.OPTIONS = options;
            if (this.form != null) {
                this.form.setControl('headers', this.createHeadersFormGroup());
                this.updateForm();
            }
            debug('set option');
        }
    }

    DELEGATE?: ITableWithFormDelegate;
    @Input() public set delegate(delegate: ITableWithFormDelegate) {
        if (delegate) {
            this.DELEGATE = delegate;
        }
    }

    DATA?: any[];
    @Input() public set data(data: any[]) {
        if (data) {
            this.DATA = data;
            this.updateForm();
        }
    }

    DISPLAYEDCOLUMNS?: any[];
    @Input() public set displayedColumns(displayedColumns: any[]) {
        if (displayedColumns) {
            this.DISPLAYEDCOLUMNS = displayedColumns;
        }
    }

    @Input()
    set additionalHeaders(value: IKeyName[]) {
        this._additionalHeaders = value;
        this.setAdditionalHeadersKeys();
    }

    get additionalHeaders(): IKeyName[] {
        return this._additionalHeaders;
    }

    @Input() firstHeaders?: IKeyName[] = [];
    @Input() language: string;
    @Input() selectedFliName: string | undefined;

    @Input() todoHighlight = true;
    @Input() isNumbered = false;
    @Input() isReferenced = false;
    @Input() tableNumber: string | undefined;

    @Input() hasFootnotes: boolean = false;

    @Input() set savedFootnote(value: string | undefined) {
        if (value !== undefined) {
            this.currentFootnoteValue = value || '';
            this.footnotes = value ? [value] : [];

            // Update form control if it exists
            if (this.form?.get('footnote')) {
                this.form.get('footnote').setValue(this.currentFootnoteValue, { emitEvent: false });
            }
        }
    }

    footnotes: string[] = [];
    currentFootnoteValue: string = '';

    @Output() formChange: EventEmitter<ITableWithForm> = new EventEmitter();
    @Output() onAddNumberingClicked: EventEmitter<void> = new EventEmitter();
    @Output() onAddReferencingClicked: EventEmitter<void> = new EventEmitter();

    protected subscriptions = [];
    form: UntypedFormGroup;
    localizedTotalText: string;
    formChangeSubscription: Subscription;
    additionalHeaderKeys: string[] = [];

    constructor(
        private formBuilder: UntypedFormBuilder,
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private i18nextService: I18NextService,
    ) {
        this.applyLocalizations();
        this.addSvgIcon('aumico-pattern-icon-remove');
        this.addSvgIcon('aumico-pattern-icon-edit-write');
    }

    private handleFootnoteChanges(): void {
        const footnoteValue = this.form.value.footnote;
        if (footnoteValue != null && footnoteValue !== '') {
            this.footnotes = [footnoteValue];
            this.currentFootnoteValue = footnoteValue;
        } else {
            this.footnotes = [];
            this.currentFootnoteValue = '';
        }
    }

    private applyLocalizations(): void {
        this.localizedTotalText = this.i18nextService.t('aumico_notes_table_with_form_total') as string;
    }

    private addSvgIcon(name: string) {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }

    private createHeadersFormGroup(): FormGroup {
        const formGroup = {};

        this.OPTIONS.properties.forEach((prop) => {
            if (prop.property !== 'remove') {
                formGroup[prop.property] = new FormControl(prop.name ?? '');
            }
        });
        return this.formBuilder.group(formGroup);
    }

    addFormGroup(dataSource: any): UntypedFormGroup {
        const dataSourceFormGroup = {};
        Object.keys(dataSource).forEach((key) => {
            if (key !== 'remove') {
                dataSourceFormGroup[key] = new UntypedFormControl(dataSource[key]);
            }
        });
        return new UntypedFormGroup(dataSourceFormGroup);
    }

    getAllAsFormArray(): UntypedFormArray {
        const formGroups = this.DATA.map((data) => this.addFormGroup(data));
        return new UntypedFormArray(formGroups);
    }

    get dataSource(): UntypedFormArray {
        return this.form.get('dataSource') as UntypedFormArray;
    }

    ngOnInit() {
        debug('ngOnInit');

        if (this.DATA.length === 0) {
            this.addRow();
        } else {
            this.updateForm();
        }
    }

    // This is added because sometimes data changes in the component itself (like fli)
    // and tableWithForm doesn't update according to that change
    ngOnChanges(change: SimpleChanges) {
        if (change['data'] && change['data'].currentValue) {
            this.DATA = change['data'].currentValue;
        }
    }

    updateForm() {
        if (this.formChangeSubscription != null) {
            this.formChangeSubscription.unsubscribe();
            this.formChangeSubscription = null;
        }

        const headers = this.form?.getRawValue().headers;

        this.form = this.formBuilder.group({
            dataSource: this.formBuilder.array([]),
            totalRow: this.hasTotalRow ? this.createTotalRowFields() : undefined,
            footnote: new UntypedFormControl(this.currentFootnoteValue || ''),
        });

        if (this.editableHeaders) {
            const headersFormGroup = headers ? this.formBuilder.group(headers) : this.createHeadersFormGroup();
            this.form.setControl('headers', headersFormGroup, { emitEvent: false });
        }

        const dataSource = this.getAllAsFormArray();
        this.form.setControl('dataSource', dataSource);

        this.formChangeSubscription = this.form.valueChanges
            .pipe(
                debounceTime(USER_INPUT_DEBOUNCE_TIME),
                distinctUntilChanged((previousFormChanges, currentFormChanges) =>
                    fastDeepEqual(previousFormChanges, currentFormChanges),
                ),
            )
            .subscribe(() => {
                this.DATA = this.form?.getRawValue().dataSource;
                debug('form change', this.form?.getRawValue());

                this.handleFootnoteChanges();

                const totalRow = this.form?.getRawValue().totalRow;
                debug('total Row Change', totalRow);

                const headers = this.form?.getRawValue().headers;
                this.formChange.emit({
                    formValue: this.form?.getRawValue().dataSource,
                    totalRowValue: totalRow,
                    headers,
                    footnote: this.currentFootnoteValue,
                });
            });
    }

    getFromControl(prop: string, row: number) {
        if (this.form) {
            return this.form.get('dataSource')['controls'][row]['controls'][prop];
        } else {
            return undefined;
        }
    }
    getFromControlOfTotalRow(prop: string) {
        if (this.form) {
            return this.form.get('totalRow')['controls'][prop];
        } else {
            return undefined;
        }
    }

    // this function is to add a tracker to the ngFor in order to be able to change mat table header
    trackByIndex(index: number) {
        return index;
    }

    createTotalRowFields(): UntypedFormGroup {
        const formGroup = {};

        this.OPTIONS.properties.forEach((property) => {
            const index = this.OPTIONS.properties.indexOf(property);
            // We check on index so that we can place the "Total" text box under the first column (property with index 0)
            if (index === 0) {
                this.totalRowTextCell = this.totalRowTextCell ?? this.localizedTotalText;
                formGroup[property.property] = new UntypedFormControl(this.totalRowTextCell, Validators.required);
            }
            if (property.hasTotal) {
                formGroup[property.property] = new UntypedFormControl(
                    { value: 0, disabled: true },
                    Validators.required,
                );
            }
        });
        return this.formBuilder.group(formGroup);
    }

    removeAt(index) {
        this.DATA.splice(index, 1);
        this.updateForm();
        debug('form change', this.form.get('dataSource').value);
        this.formChange.emit({
            formValue: this.form.get('dataSource').value,
            totalRowValue: this.form.getRawValue().totalRow,
            headers: this.form.getRawValue().headers,
        });
    }

    addRow() {
        const emptyRow = {};
        this.OPTIONS.properties.forEach((property) => {
            emptyRow[property.property] = '';
        });
        this.DATA.push(emptyRow);
        this.updateForm();
    }

    onTitleChange(newTitle: string, formControlName: string) {
        this.form.get('headers').get(formControlName).setValue(newTitle);
        this.updateForm();
        const totalRow = this.form?.getRawValue().totalRow;

        const headers = this.form?.getRawValue().headers;
        this.formChange.emit({
            formValue: this.form?.getRawValue().dataSource,
            totalRowValue: totalRow,
            headers,
        });
    }

    onAddNumberingClick(): void {
        this.onAddNumberingClicked.emit();
    }

    onAddReferencingClick(): void {
        this.onAddReferencingClicked.emit();
    }

    onFileUploadClick(index) {
        this.DELEGATE.onFileUploadClick(index);
    }
    hasFooter(property: IProperty): boolean {
        const index = this.OPTIONS.properties.indexOf(property);
        // We check on index so that we can place the "Total" text box under the first column (property with index 0)
        if (index === 0 || property.hasTotal) {
            return true;
        }
        return false;
    }

    getFooterCellContent(property: IProperty): string {
        if (property.hasTotal) {
            const entriesValue = this.DATA.map<number | null>((data) => data[property.property]);
            const sum = NumberUtils.sumIgnoringInfiniteNumbers(entriesValue, 0);

            this.form.get('totalRow').get(property.property).setValue(sum, { emitEvent: false });
            this.formChange.emit({
                formValue: this.form.get('dataSource').value,
                totalRowValue: this.form.getRawValue().totalRow,
                headers: this.form.getRawValue().headers,
            });
            return sum.toString();
        } else {
            try {
                return this.form.get('totalRow').get(property.property).value;
            } catch (error) {
                return '';
            }
        }
    }

    public compareObjects(o1: IPropertyOption, o2: IPropertyOption): boolean {
        return o1.id === o2.id;
    }

    public getAddNewItemText() {
        if (this.OPTIONS.addNewItemText) {
            return this.OPTIONS.addNewItemText;
        } else {
            return 'aumico_notes_table_with_form_add_new_item';
        }
    }

    setAdditionalHeadersKeys() {
        this.additionalHeaderKeys = [];

        if (this.additionalHeaders != null) {
            this.additionalHeaders.forEach((header) => {
                this.additionalHeaderKeys.push(header.key);
            });
        }
    }
    getFirstHeadersKeys() {
        const keys = [];

        if (this.firstHeaders && this.firstHeaders.length) {
            this.firstHeaders.forEach((header) => {
                keys.push(`f-${header.key}`);
            });
        }

        return keys;
    }
    public getMandatoryText() {
        if (this.OPTIONS.mandatoryText) {
            return this.OPTIONS.mandatoryText;
        } else {
            return 'This field is mandatory.';
        }
    }
    public getInvalidText() {
        if (this.OPTIONS.invalidText) {
            return this.OPTIONS.invalidText;
        } else {
            return 'This field is invalid.';
        }
    }
}
